import { createContext } from "react";
import type { MeDto } from "@/types/api";

/**
 * The auth context payload exposed by `<AuthProvider>` in
 * `@/hooks/useAuth`. Lives in its own module so consumers that only
 * need to *read* it (e.g. `useUserTimeZone` in `@/utils/datetime`)
 * don't trip on the dozens of test files that `vi.mock("@/hooks/useAuth")`
 * and replace the entire module surface.
 */
export interface AuthContextValue {
  user: MeDto | null;
  loading: boolean;
  error: string | null;
  /** Effective primary role: the admin-assigned one when set, else the
   *  deterministic precedence fallback (`@/lib/auth/rolePrecedence`).
   *  Replaces the old `user.roleCodes[0]` guess. */
  primaryRoleCode: string | null;
  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (perms: string[]) => boolean;
  hasRoleCode: (code: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
